import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { DesignerPageContext } from '../../DesignerPage';
import './SelectedModel.scss';
import CircularSlider from './CircularSlider';
import * as THREE from 'three';
import { CabinetTrimPos, InsideType } from '../../../../constants/enum';
import { Icon } from '../../../../components/icon/icon';

const SelectedModel = () => {
  const designerPageContext = useContext(DesignerPageContext);
  const [position, setPosition] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [angle, setAngle] = useState(0);
  const modelRef = useRef(null);

  // Constants được memoize
  const CONSTANTS = useMemo(
    () => ({
      RAD_TO_DEG: 180 / Math.PI,
      DEG_TO_RAD: Math.PI / 180,
    }),
    []
  );

  // Utility functions được optimize
  const sceneManager = designerPageContext.sceneManager;
  const textureController = sceneManager?.textureController;
  const positionController = sceneManager?.positionController;

  // Helper function để update module position
  const updateModulePosition = useCallback(
    moduleGroup => {
      if (!positionController || !moduleGroup?.userData?.moduleData) return;

      const {
        userData: { moduleData },
        position,
        rotation,
        uuid,
      } = moduleGroup;

      const cloneModule = moduleGroup.clone();
      cloneModule.children = [
        cloneModule.children.find(x => x.type === 'Group'),
      ];
      cloneModule.rotation.set(0, 0, 0);
      cloneModule.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(cloneModule);
      const size = new THREE.Vector3();
      box.getSize(size);
      const center = new THREE.Vector3();
      box.getCenter(center);
      positionController.updateModule(uuid, {
        id: uuid,
        x: center.x,
        y: center.y - size.y / 2,
        z: center.z,
        width: size.x,
        height: size.y,
        depth: size.z,
        rotate: rotation,
      });

      const newSelectedModels = [
        ...designerPageContext.selectedModels.filter(
          m => m.uuid !== moduleGroup.uuid
        ),
        moduleGroup,
      ];
      designerPageContext.setSelectedModels([...newSelectedModels]);
    },
    [positionController, designerPageContext]
  );

  // Helper function để update UI state
  const updateUIState = useCallback(
    (model, screenPos, shouldShow = true) => {
      if (shouldShow && model) {
        setPosition(screenPos);
        setVisible(true);
        modelRef.current = model;
        setAngle(model.rotation.y * CONSTANTS.RAD_TO_DEG);
        // Bước 1: tìm module tương ứng trong kitchenCabinet
        const cabinet =
          designerPageContext.sceneManager?.cabinetManager?.currentCabinet;
        if (cabinet) {
          const foundModule = cabinet.getModuleByGroupUuid(model.uuid);
          if (foundModule) {
            cabinet.setActiveEditorModuleByUuid(model.uuid);
          }
        } else {
          setVisible(false);
          modelRef.current = null;
        }
      } else {
        setVisible(false);
        modelRef.current = null;
      }
    },
    [CONSTANTS.RAD_TO_DEG, designerPageContext.sceneManager]
  );

  // Helper function để clear selections
  const clearAllSelections = useCallback(() => {
    // textureController?.unregisterAllModules();

    designerPageContext.setSelectedModels([]);
    designerPageContext.setSelectedModel(null);

    positionController?.deselectModule();

    const kitchenCabinet = designerPageContext.sceneManager.kitchenCabinet;
    if (kitchenCabinet) {
      kitchenCabinet.activeEditorModule = null;
    }
  }, [textureController, positionController]);

  // Multi-select logic được tối ưu
  const handleMultiSelect = useCallback(
    (model, screenPos) => {
      const currentSelected = designerPageContext.selectedModels;
      const isAlreadySelected = currentSelected.some(
        m => m.uuid === model.uuid
      );

      let newSelectedModels;
      designerPageContext.setSelectedModel(null);
      designerPageContext.meshClickRef.current = null;
      if (isAlreadySelected) {
        // Bỏ chọn model
        newSelectedModels = currentSelected.filter(m => m.uuid !== model.uuid);
        clearAllSelections();
        updateUIState(null, null, false);
      } else {
        // Thêm model vào selection
        newSelectedModels = [...currentSelected, model];
        positionController?.selectModule(model.uuid);
        // designerPageContext.setSelectedModel(model);
        updateUIState(model, screenPos, false);
      }

      // Cập nhật state
      designerPageContext.setSelectedModels([...newSelectedModels]);

      // // Batch register các modules
      // newSelectedModels.forEach((selectedModel) => {
      //   textureController?.registerModule(selectedModel.uuid, selectedModel);
      // });
    },
    [
      designerPageContext,
      clearAllSelections,
      updateUIState,
      positionController,
      textureController,
    ]
  );

  // Single select logic được tối ưu
  const handleSingleSelect = useCallback(
    (model, screenPos) => {
      if (!model) return;
      const currentSelected = designerPageContext.selectedModels;
      const isAlreadySelected = currentSelected.some(
        m => m.uuid === model.uuid
      );

      if (isAlreadySelected) return;

      // Cập nhật states
      designerPageContext.setSelectedModel(model);
      designerPageContext.setSelectedModels([model]);
      updateUIState(model, screenPos);

      //Xét meshclick về null
      designerPageContext.meshClickRef.current = null;

      // Register module
      // textureController?.registerModule(model.uuid, model);
      positionController?.selectModule(model.uuid);
      // Optional: Set callback để listen distance changes
      positionController.setOnDistanceChanged(async data => {
        // if (modelRef.current) {
        //   modelRef.current.position.x = data.x;
        //   modelRef.current.position.z = data.z;
        //   modelRef.current.position.y = data.y;
        // }

        setTimeout(() => {
          if (designerPageContext.sceneManager?.borderSelectedModule?.length) {
            designerPageContext.sceneManager.removeBorderSelectedModule();
            designerPageContext.sceneManager.handleDrawBorderSelectedModule(
              model
            );
          }
          designerPageContext.sceneManager?.handleDrawBorder();
        }, 0);
        // data contains:
        // - moduleId: ID của module
        // - direction: Hướng (x1, x2, z1, z2)
        // - oldDistance: Khoảng cách cũ
        // - newDistance: Khoảng cách mới
        // - newPosition: Vị trí mới {x, z}
      });

      const selectedModuleData =
        designerPageContext.sceneManager.cabinetManager.getModuleByGltfUuid(
          model.uuid
        );

      let beveled = false;
      if (selectedModuleData?.listInside?.length > 0) {
        selectedModuleData.listInside.forEach(inside => {
          if (inside.type === InsideType.DOOR) {
            beveled = inside.beveled;

            return;
          }
        });
      }

      designerPageContext.insideLoaderRef.current.changeBeveledInside(beveled);
    },
    [designerPageContext, updateUIState, textureController, positionController]
  );

  // Main select handler
  const onSelect = useCallback(
    (model, screenPos, event) => {
      // Clear tất cả modules trước
      // textureController?.unregisterAllModules();

      const isShiftPressed = event?.shiftKey || false;

      if (isShiftPressed) {
        handleMultiSelect(model, screenPos);
      } else {
        handleSingleSelect(model, screenPos);
      }
    },
    [textureController, handleMultiSelect, handleSingleSelect]
  );

  // Deselect handler được tối ưu
  const onDeselect = useCallback(() => {
    setVisible(false);
    setIsRotating(false);
    modelRef.current = null;
    clearAllSelections();
  }, [designerPageContext, clearAllSelections]);

  // Drag handlers được tối ưu
  const onDragging = useCallback(() => {
    sceneManager.removeBorderSelectedModule();
    setVisible(false);

    // Xoá tất cả MB, VB nếu di chuyển module bếp dưới
    if (
      modelRef.current?.userData?.cabinetType?.includes('kitchen') &&
      modelRef.current?.userData?.moduleType?.includes('lower')
    ) {
      sceneManager?.clearAllCountertopAndBacksplash();
    }

    setTimeout(() => {
      //Xoá border của model được drag
      sceneManager?.handleDrawBorder(modelRef.current);
      sceneManager?.removeBorderErrorMesh();
    }, 0);
  }, [positionController]);

  const onDragEnd = useCallback(
    screenPos => {
      setVisible(true);
      setPosition(screenPos);

      // Vẽ border và cập nhật giao diện khi drag xong
      designerPageContext.setTick(prev => prev + 1);

      const moduleGroup = sceneManager?.dragController?.selectedModel;
      if (moduleGroup) {
        updateModulePosition(moduleGroup);
      }

      setTimeout(async () => {
        // Cập nhật lại MB,VB
        await sceneManager.updateAllCountertopAndBacksplash();

        // if (sceneManager?.dimensionSystem?.objects.length > 0 && sceneManager) {
        //   sceneManager.clearAllCornerPoints();
        //   sceneManager.generateCornerPoints();
        // }

        if (designerPageContext.selectedModels.length) {
          sceneManager.removeBorderSelectedModule();

          sceneManager.errorModule.forEach(group =>
            sceneManager.handleDrawBorderSelectedModule(group)
          );
        }
        await sceneManager?.calculateTotalCost?.({});
      }, 0);
    },
    [sceneManager, updateModulePosition]
  );

  const handleAngleChange = useCallback(
    newAngle => {
      setAngle(newAngle);
      positionController?.clearDistanceLines();

      if (modelRef.current) {
        modelRef.current.rotation.y = newAngle * CONSTANTS.DEG_TO_RAD;
        updateModulePosition(modelRef.current);
      }
    },
    [CONSTANTS.DEG_TO_RAD, updateModulePosition, positionController]
  );

  const handleRotationKeyPress = useCallback(
    event => {
      if (event.code === 'KeyR') {
        setAngle(prevAngle => {
          designerPageContext.setTick(t => t + 1);

          const newAngle = prevAngle + 90;

          if (modelRef.current) {
            modelRef.current.rotation.y = newAngle * CONSTANTS.DEG_TO_RAD;
            updateModulePosition(modelRef.current);
          }

          positionController?.clearDistanceLines();

          return newAngle;
        });

        setTimeout(async () => {
          // Cập nhật lại MB,VB
          await sceneManager.updateAllCountertopAndBacksplash();

          // if (
          //   sceneManager?.dimensionSystem?.objects.length > 0 &&
          //   sceneManager
          // ) {
          //   sceneManager.clearAllCornerPoints();
          //   sceneManager.generateCornerPoints();
          // }

          if (designerPageContext.selectedModels.length) {
            sceneManager.removeBorderSelectedModule();

            designerPageContext.selectedModels.forEach(group =>
              sceneManager.handleDrawBorderSelectedModule(group)
            );
          }
        }, 0);
      }
    },
    [CONSTANTS.DEG_TO_RAD, updateModulePosition, positionController]
  );

  // Setup event listeners
  useEffect(() => {
    if (!sceneManager) return;

    const eventHandlers = {
      onSelect,
      onDeselect,
      onDragging,
      onDragEnd,
    };

    sceneManager.setSelectedModelUIEvents(eventHandlers);

    // Cleanup function (nếu cần)
    return () => {
      // sceneManager.clearSelectedModelUIEvents?.();
    };
  }, [sceneManager, onSelect, onDeselect, onDragging, onDragEnd]);

  useEffect(() => {
    if (designerPageContext.selectedModel === null) {
      setVisible(false);
      setPosition(null);
    }

    //Nhân R để quay model
    const onKey = e => handleRotationKeyPress(e);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [designerPageContext.selectedModel]);

  // Early return nếu không visible
  if (!visible || !position) return null;

  return (
    <div
      className="model-ui-buttons"
      style={{
        top: position.y,
        left: position.x,
        transform: 'translate(-50%, -100%)', // Center the UI
      }}
    >
      {isRotating ? (
        <div className="rotate-ring-ui p-3">
          <CircularSlider
            size={80}
            initialAngle={angle}
            onAngleChange={handleAngleChange}
            onAngleChangeStart={() => {
              setTimeout(async () => {
                // Cập nhật lại MB,VB khi bắt đầu xoay model
                await sceneManager.updateAllCountertopAndBacksplash();
              }, 0);
            }}
            modelRef={modelRef}
          />
        </div>
      ) : (
        <div className="button-group">
          <button
            className="btn-move"
            onPointerDown={e => {
              if (designerPageContext.selectedModel) {
                const dragModuleData =
                  designerPageContext.sceneManager.cabinetManager.getModuleByGltfUuid(
                    designerPageContext.selectedModel?.uuid
                  );

                positionController?.clearDistanceLines();
                designerPageContext.sceneManager.startDragSelectedModel(
                  e,
                  'xz',
                  dragModuleData
                );

                sceneManager.clearAllCornerPoints();
              }
            }}
          >
            <span className="w-[24px] h-[24px] flex">
              <Icon.ICMove />
            </span>
          </button>

          <button
            className="btn-up"
            onPointerDown={e => {
              positionController?.clearDistanceLines();
              designerPageContext.sceneManager.startDragSelectedModel(e, 'y');
            }}
          >
            <span className="w-[24px] h-[24px] flex">
              <Icon.ICHeight />
            </span>
          </button>

          {/* <button
            className="btn-o"
            onPointerDown={e => {
              positionController?.clearDistanceLines();
              designerPageContext.sceneManager.startDragSelectedModel(e, 'o');
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M7 17L2 12L7 7L8.4 8.4L5.825 11H18.175L15.6 8.4L17 7L22 12L17 17L15.6 15.6L18.175 13H5.825L8.4 15.6L7 17Z"
                fill="#2B2E31"
              />
            </svg>
          </button> */}

          <button
            className="btn-rotate"
            onClick={e => {
              setIsRotating(true);
            }}
            type="button"
            aria-label="Rotate"
          >
            <span className="w-[24px] h-[24px] flex">
              <Icon.ICRotateLeft />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(SelectedModel);
